/**
 * Read-adapter from the real `payroll.listEmployees` wire shape to the UI's
 * `Employee` type (C3, #21934). Pure, no React — mirrors
 * `wagepro_store_session_facts.ts`'s `deriveSessionFromFacts` pattern:
 * `RealEmployeeRecord` is the permanent adapter shape (survives the #21929
 * employer-admin OAuth scope work unchanged; only HOW records get resolved
 * changes — `use_employees.ts` is today's real, self-fetching resolver,
 * having replaced the interim dev-bypass-only `dev_employees.ts`, which PR
 * #22369 deleted), and `deriveEmployeesFromRecords` is the permanent seam
 * the store calls.
 *
 * ── Read-path only ─────────────────────────────────────────────────────────
 *
 * This adapter only ever maps FROM `payroll.listEmployees`'s real rows TO the
 * UI shape; it is never invoked in the write direction. The create/edit
 * employee form (#22335, `screens/employees/employee_form_dialog.tsx`) reads
 * `RealEmployeeRecord` directly (via `use_employees.ts`'s
 * `useEmployeeRecord()`) rather than through this adapter — the UI's
 * `Employee` type drops/masks fields (bank account, `pay_basis`,
 * `hourly_rate_cents`, NIC/EPF) the edit form needs verbatim, so the form's
 * own `employee_form_validation.ts` builds `createEmployee`/`updateEmployee`
 * wire input straight from the raw record instead of round-tripping through
 * `Employee`. This module (`deriveEmployeesFromRecords`/`toUiEmployee`)
 * remains genuinely read-only and unchanged by that work.
 *
 * ── Documented gaps (do not silently drop) ─────────────────────────────────
 *
 * - `pay_frequency` is always `'monthly'` — the backend has no frequency
 *   concept.
 * - `salary_structure` is always `'—'` — no backend equivalent.
 * - Bank `verified` is always `false` — no backend concept; the issue's own
 *   guidance is "always show unverified" rather than inventing one.
 * - `status` never produces `'on_leave'` — no backend concept for that
 *   employment state distinct from active/terminated.
 * - `status_history` only ever contains a single synthetic `'joined'` event
 *   (when `employment_start_date` is known) — no `'terminated'` event,
 *   because that would need a reason code (redundancy/resignation) the
 *   backend does not record.
 * - `tin` is always `''` — only read by the dead `TaxTab`
 *   (`profile_tabs/bank_tax_tabs.tsx`), which is imported nowhere post-#21919
 *   and therefore never rendered.
 * - `documents`/`assets`/`performance`/`loans` are always `[]` — the tabs
 *   that rendered these were removed by #21919; the fields remain on
 *   `Employee` for now.
 */
import type {
  ApitEmploymentType,
  BankDetails,
  Employee,
  EmploymentStatus,
  EmploymentType,
  TaxResidency,
} from './types';
import { deriveTintFromKey } from './wagepro_store_session_facts';

/**
 * The wire shape `payroll.listEmployees` returns, narrowed to exactly the
 * fields this adapter reads. The real procedure returns the full Drizzle
 * `employees` row (more fields than this — statutory columns, audit-only
 * bookkeeping, ...); this interface is intentionally a subset, not a
 * mirror, so it stays stable across backend-only column additions.
 */
export interface RealEmployeeRecord {
  id: string;
  full_name: string;
  email: string | null;
  /**
   * The platform user id this employee row is linked to (nullable — set only
   * once the employee has accepted an ESS invite, see `entity_users.ts`'s
   * `employee_user_id` doc comments), or `null` for a not-yet-linked/invited
   * employee. Read (never written) by `wagepro_store_expenses.ts` (#22792)
   * to resolve an expense claim's `submitter_user_id` back to the submitting
   * employee's `full_name` -- already present on the real wire row (a bare
   * `select()` of the full `employees` table), just not previously read by
   * this intentionally-narrower adapter interface.
   */
  employee_user_id: string | null;
  pay_basis: 'salaried' | 'hourly';
  monthly_salary_cents: number | null;
  hourly_rate_cents: number;
  /**
   * V381 — currency override for this employee's pay (`hourly_rate_cents` /
   * `monthly_salary_cents`). `null` = "no override", resolves to the
   * entity's base currency (`resolveEmployeePayCurrency` on the API side;
   * `employee_form_validation.ts`'s `employeeRecordToFormDraft` does the
   * equivalent resolution for the form draft here).
   */
  pay_currency_code: string | null;
  employment_start_date: string | null;
  employment_end_date: string | null;
  termination_date: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_branch: string | null;
  /** Bank routing / SWIFT code. Null if not provided (#22335). */
  bank_code: string | null;
  is_active: boolean;
  created_at: string;
  /** Sri Lanka EPF member number for statutory returns. Null until recorded (#22335, the edit form needs this verbatim -- the read-only `Employee` UI type never carried it). */
  epf_member_no: string | null;
  /** Employer-assigned employee number (MIGRATION_V395), unique per entity when set. Null until recorded. Read verbatim by the edit form. */
  employee_number: string | null;
  /** Sri Lanka National Identity Card number. Null until recorded (#22335). */
  nic_number: string | null;
  /** Indonesia NPWP (tax ID). Null until recorded (#22335). */
  npwp: string | null;
  /** Indonesia NIK (national ID). Null until recorded (#22335). */
  nik: string | null;
  /**
   * Indonesia PTKP marital/dependent status. Untyped `string | null` (not a
   * closed `PtkpStatus` union) deliberately, mirroring `employment_type`'s
   * same free-text-column stance -- `employee_form_validation.ts`'s
   * `employeeRecordToFormDraft` validates it explicitly against the known
   * PTKP set rather than trusting the wire value (#22335).
   */
  ptkp_status: string | null;
  /** Indonesia BPJS JKK risk class (1-5). Null until recorded (#22335). */
  bpjs_jkk_risk_class: number | null;
  /** Free text. Null until recorded (MIGRATION_V281_STEPS). */
  job_title: string | null;
  /** Free text. Null until recorded (MIGRATION_V281_STEPS). */
  department: string | null;
  /** Free text ("reports to"). Null until recorded (MIGRATION_V281_STEPS). */
  manager: string | null;
  /** Free text. Null until recorded (MIGRATION_V281_STEPS). */
  work_location: string | null;
  /**
   * Untyped `string | null` (not `EmploymentType | null`) deliberately: the
   * DB column is free-text TEXT (matching `ptkp_status`'s pattern), not a
   * SQL CHECK-constrained enum, so a legacy or out-of-band row could in
   * principle hold a value outside the closed set the tRPC input boundary
   * enforces going forward. This adapter validates it explicitly — see
   * {@link deriveEmploymentType} — rather than trusting the wire type.
   */
  employment_type: string | null;
  /** Part-time FTE in basis points (MIGRATION_V384). Null = not part-time / 100%. */
  fte_bps: number | null;
  /**
   * SL tax residency (V283). Untyped free text, validated by
   * {@link deriveResidencyStatus}. Optional: the interface is a subset of the
   * wire row and stays stable across backend column additions (#26653).
   */
  residency_status?: string | null;
  /** Sri Lankan citizenship (V283); with `residency_status` selects IRD Table 4. */
  is_citizen?: boolean | null;
  /** IRD Table 07 routing (V436, ADR-168). Free text, validated by {@link deriveApitEmploymentType}. */
  apit_employment_type?: string | null;
  /** Declared monthly primary-employment income, cents (V436); null = none declared. */
  apit_declared_primary_income_cents?: number | null;
  /** Employer-borne income tax, IRD Table 06 (V437, ADR-170). */
  apit_borne_by_employer?: boolean | null;
}

const VALID_EMPLOYMENT_TYPES: readonly EmploymentType[] = [
  'full_time',
  'part_time',
  'contract',
  'contractor',
];

/**
 * Validates `employment_type` against the closed set the UI's
 * `EMPLOYMENT_TYPE_KEY` i18n lookup (`lib/label_keys.ts`) requires — an
 * unrecognised value there renders as `undefined`/a raw key, so this must
 * always return a valid `EmploymentType`, never pass through an arbitrary
 * string. Defaults to `'full_time'` (documented, not fabricated data — the
 * UI has no "unknown" employment-type state to render instead).
 */
export function deriveEmploymentType(value: string | null): EmploymentType {
  return value !== null &&
    VALID_EMPLOYMENT_TYPES.includes(value as EmploymentType)
    ? (value as EmploymentType)
    : 'full_time';
}

/** Validates a raw `residency_status`; anything unrecognised reads as the backend default `'resident'` (never trusted, like {@link deriveEmploymentType}). */
export function deriveResidencyStatus(
  value: string | null | undefined,
): TaxResidency {
  return value === 'non_resident' ? 'non_resident' : 'resident';
}

/** Validates a raw `apit_employment_type`; anything unrecognised reads as the backend default `'primary'`. */
export function deriveApitEmploymentType(
  value: string | null | undefined,
): ApitEmploymentType {
  return value === 'secondary' ? 'secondary' : 'primary';
}

/**
 * `employment_start_date` is shape-validated at the API boundary
 * (`ISO_DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)`) but not
 * calendar-validated — `'2026-13-40'` passes that regex and persists.
 * `format_date` (lib/format.ts) does `new Date(`${date}T00:00:00Z`)` and
 * throws a RangeError on the resulting Invalid Date, white-screening the Job
 * tab. Mirrors `deriveEmploymentType`'s "never trust the wire value, always
 * return something safe to render" stance.
 *
 * Exported so `wagepro_store_pay_runs.ts` (C4a, #21954) can apply the exact
 * same defensive check to `pay_runs.period_start`/`period_end` — those are
 * shape- but not calendar-validated the same way, and share the same
 * `format_date`/`format_month_year` RangeError failure mode.
 */
export function isParseableIsoDate(value: string): boolean {
  return !isNaN(new Date(`${value}T00:00:00Z`).getTime());
}

/**
 * A validated `employment_start_date`, or `null` when it's absent OR
 * calendar-invalid — treated identically so a bad value degrades exactly
 * like the already-handled null case rather than crashing or fabricating a
 * date from `created_at` (which reflects row creation, not actual
 * employment start, and would be misleading in `status_history`).
 */
function deriveValidStartDate(record: RealEmployeeRecord): string | null {
  return record.employment_start_date !== null &&
    isParseableIsoDate(record.employment_start_date)
    ? record.employment_start_date
    : null;
}

/** First-name + last-name initials, uppercased. Falls back to '—' for an empty/whitespace-only name (should not happen — `full_name` is NOT NULL — but never throw over it). */
function deriveInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts.at(0)?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : '';
  const initials = `${first}${last}`.toUpperCase();
  return initials.length > 0 ? initials : '—';
}

/**
 * `'terminated'` when `termination_date` is recorded OR the row is soft-
 * deactivated (`is_active === false`); `'active'` otherwise. `'on_leave'` is
 * never produced — see the module docblock's documented-gaps section.
 */
function deriveStatus(record: RealEmployeeRecord): EmploymentStatus {
  return record.termination_date !== null || !record.is_active
    ? 'terminated'
    : 'active';
}

/**
 * Masks a bank account number to the mock data's exact display format
 * (`'•••• •••• 7742'` — see `mock_data.ts`): non-digit separators stripped,
 * only the last 4 digits shown. `null`/empty falls back to `'—'` rather than
 * an empty mask, matching every other unset-field fallback in this adapter.
 */
function maskAccountNumber(accountNumber: string | null): string {
  if (!accountNumber) return '—';
  const digits = accountNumber.replace(/\D/g, '');
  if (digits.length === 0) return '—';
  const last4 = digits.length >= 4 ? digits.slice(-4) : digits;
  return `•••• •••• ${last4}`;
}

function toUiBank(record: RealEmployeeRecord): BankDetails {
  return {
    bank_name: record.bank_name ?? '—',
    account_masked: maskAccountNumber(record.bank_account_number),
    branch: record.bank_branch ?? '—',
    // Issue guidance: no `verified` concept exists server-side today —
    // always show unverified rather than inventing a signal.
    verified: false,
  };
}

function toUiEmployee(record: RealEmployeeRecord): Employee {
  const job_title = record.job_title ?? '—';
  const valid_start_date = deriveValidStartDate(record);

  return {
    id: record.id,
    name: record.full_name,
    initials: deriveInitials(record.full_name),
    tint: deriveTintFromKey(record.id),
    department: record.department ?? '—',
    job_title,
    employment_type: deriveEmploymentType(record.employment_type),
    // Only a part-timer carries an FTE; anything else on the wire is
    // ignored so a stale value can never render as "50% of full-time" on a
    // full-time worker.
    fte_bps:
      deriveEmploymentType(record.employment_type) === 'part_time'
        ? record.fte_bps
        : null,
    residency_status: deriveResidencyStatus(record.residency_status),
    is_citizen: record.is_citizen ?? true,
    apit_employment_type: deriveApitEmploymentType(record.apit_employment_type),
    apit_declared_primary_income_cents:
      record.apit_declared_primary_income_cents ?? null,
    apit_borne_by_employer: record.apit_borne_by_employer ?? false,
    status: deriveStatus(record),
    manager: record.manager,
    // `format_date` (lib/format.ts) does `new Date(`${date}T00:00:00Z`)` and
    // throws RangeError on an unparseable date -- `employment_start_date` is
    // nullable AND only shape- (not calendar-) validated at the API boundary
    // (see `deriveValidStartDate`), but `created_at` is NOT NULL on every
    // row, so slicing its date portion is always a safe, always-parseable
    // fallback (never an empty string, which WOULD white-screen the Job tab).
    start_date: valid_start_date ?? record.created_at.slice(0, 10),
    work_location: record.work_location ?? '—',
    // Documented gap: backend has no pay-frequency concept (monthly implied).
    pay_frequency: 'monthly',
    pay_basis: record.pay_basis,
    // An hourly employee has no per-period salary figure server-side, so
    // this is 0 for them -- `pay_basis` is what tells the UI whether the
    // number means anything. Surfacing it unconditionally is exactly the
    // "Basic salary LKR 0" defect fixed in #24396.
    basic_salary: record.monthly_salary_cents ?? 0,
    hourly_rate: record.hourly_rate_cents,
    pay_currency_code: record.pay_currency_code,
    // Documented gap: no salary_structure concept server-side.
    salary_structure: '—',
    bank: toUiBank(record),
    // Documented gap: only read by the dead TaxTab (imported nowhere
    // post-#21919) -- always empty.
    tin: '',
    status_history: valid_start_date
      ? [
          {
            kind: 'joined',
            date: valid_start_date,
            role: job_title,
          },
        ]
      : [],
    documents: [],
    assets: [],
    performance: [],
    loans: [],
  };
}

/**
 * Derives the UI's employee list from real backend records.
 *
 * Used to also take a `mock` fallback parameter (returned unchanged when
 * `records` was `undefined`, mirroring `deriveSessionFromFacts`'s
 * "absent input -> no-op" contract). That parameter was removed in PR
 * #22369's adversarial review pass (finding #10): `use_employees.ts` is this
 * function's only call site, and it only ever calls this once `records` has
 * already resolved to a real, defined array (see `resolveEmployeesState`) —
 * the mock-passthrough branch was consequently dead code, not a reachable
 * fallback, and `MOCK_EMPLOYEES` is unreachable from any render path. An
 * explicitly EMPTY array is still a real, distinct state from "no data" --
 * a genuinely employee-less entity renders the directory's existing
 * `EmptyState`, it never fabricates a mock roster.
 */
export function deriveEmployeesFromRecords(
  records: RealEmployeeRecord[],
): Employee[] {
  return records.map(toUiEmployee);
}
